import produce from "immer";

import { UseImportReferenceData } from "../use-import-reference-data";
import { ImportFunctionResult, ImportTemplateData, TemplateImporter, TemplateImportState, Tuple, ProductionImportState as ImportState } from "../types";
import { getStringCellValue } from '../utils';
import { Production } from "psims/models/submission-types/production";
import { isValidForPeriod } from "psims/models/ref-data/util";
import { HasReportingPeriod } from "psims/react/pages/primary-pages/data-submissions/shared/types";

const headerKeyColumns = ['Product group', 'Product'];

const getBiofuelProductionProductId = (
    page: string,
    refData: UseImportReferenceData,
    columns: Array<Tuple<string, number | null>>,
    row: any[],
    dataSubmission: HasReportingPeriod
): ImportFunctionResult => {
    const productGroup = getStringCellValue(row, columns, 'Product group', true);
    const product = getStringCellValue(row, columns, 'Product', true);
    const productionType = 'Biofuel production';
    const productionProduct = refData.getProductionProduct(productionType, (productGroup ?? '').trim(), (product ?? '').trim());
    const productionProductGroup = refData.getProductionProductGroup(productionType, (productGroup ?? '').trim().trim());

    if (productionProduct == null || productionProductGroup == null) {
        return {
            isSuccessful: false,
            error: {
                error: `Product not found [${product}]`,
                page,
            }
        };
    }

    // Check product isn't expired
    const isExpired = !(
        isValidForPeriod(productionProduct, dataSubmission.reportingPeriodFrom as string, dataSubmission.reportingPeriodTo as string) &&
        isValidForPeriod(productionProductGroup, dataSubmission.reportingPeriodFrom as string, dataSubmission.reportingPeriodTo as string)
    );

    if (isExpired) {
        return {
            isSuccessful: false,
            error: {
                error: `The product ${productionProduct.productName} is not active for the reporting period.`,
                isTerminalError: true,
                page,
                recommendTemplateRequest: true,
            }
        }
    }

    const result = productionProduct?.id;

    return {
        isSuccessful: true,
        result
    };
};

const getBiofuelOrganisationProductionAreaId = (page: string, refData: UseImportReferenceData, columns: Array<Tuple<string, number | null>>, row: any[]): ImportFunctionResult => {
    const productionArea = getStringCellValue(row, columns, 'Facility name', true);
    const result = refData.getOrganisationProductionAreaId(productionArea ?? '');

    if (result == null) {
        return {
            isSuccessful: false,
            error: {
                error: `Facility name not found [${productionArea}]`,
                page,
            }
        };
    }

    return {
        isSuccessful: true,
        result
    };
};

const biofuelProductionImport = (): TemplateImporter<ImportState> => {
    const newSubmissionBuilder: () => ImportState = () => ({
        productions: [],
        pageData: {
            pageSaved: false,
        }
    });

    const importStateBuilder: () => TemplateImportState<ImportState> = () => ({
        templateImportDialogState: 'processing',
        unsavedChanges: false,
        data: newSubmissionBuilder(),
    });
    
    const productionInputs: ImportTemplateData<Partial<Production>> =  {
        dataKey: 'productions',
        page: {
            name: 'Biofuel production',
            headerRowKeys: headerKeyColumns,
            columns: [
                { column: 'Product', dataKey: 'productionProductId', dataType: 'string', colFunction: getBiofuelProductionProductId, },
                { column: 'Facility name', dataKey: 'organisationProductionAreaId', dataType: 'string', colFunction: getBiofuelOrganisationProductionAreaId, },
                { column: 'Average density (Kg/L)', dataKey: 'productionDensity', dataType: 'decimal', decimalPoints: 10 },
                { column: 'Opening stocks (Kilolitres)', dataKey: 'openingStocks', dataType: 'integer', },
                { column: 'Total production (Kilolitres)', dataKey: 'produced', dataType: 'integer', },
                { column: 'Closing stocks (Kilolitres)', dataKey: 'closingStocks', dataType: 'integer', },
            ],
            data: []
        } 
    };

    const mergeRows = (importState: TemplateImportState<ImportState>, data: Array<Partial<Production>>): Array<Partial<Production>> => {
        const newData: Array<Partial<Production>> = data;
        const existingRows = importState.data?.productions ?? [];
        existingRows.forEach(d => {
            const idx = newData.findIndex(x => (x.productionProductId ?? 0) === (d.productionProductId ?? 0) && (x.organisationProductionAreaId ?? 0) === (d.organisationProductionAreaId ?? 0));
            if (idx === -1) {
                newData.push(d);
            }
        })
        return newData;
    };

    const setStepSaved = (importState: TemplateImportState<ImportState>, stepName: any): TemplateImportState<ImportState> => {
        if (stepName === 'Biofuel production' && importState.data && importState.data.pageData) {
            const newMetadata = produce(importState.data.pageData, draft => {
                draft.pageSaved = true;
            });
            
            return {
                ...importState,
                data: {
                    ...importState.data,
                    pageData: newMetadata,
                },
                unsavedChanges: false,
            };
        }

        if (stepName === 'Submit' && importState.data && importState.data.pageData) {
            const hasUnsavedChanges = !importState.data.pageData.pageSaved;
            if (!hasUnsavedChanges) {
                return {
                    templateImportDialogState: 'idle',
                    unsavedChanges: false,
                    submitSaved: false,
                    data: undefined
                };
            }
            return {
                ...importState,
                submitSaved: true
            };
        }
        return importState;
    }

    const savedSteps = (importState: TemplateImportState<ImportState>) => {
        return importState?.data?.pageData;
    }

    const importer: TemplateImporter<ImportState> = {
        importStateBuilder,
        dataElements: [productionInputs],
        mergeRows,
        savedSteps,
        setStepSaved
    }
    return importer;
};

export default biofuelProductionImport;
